'use client'

import { Button, isButtonVariant } from '@syconium/little-miss-figgy/dist/components/Button'
import { ResponsiveImage } from '@syconium/little-miss-figgy/dist/components/Image/ResponsiveImage'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { PageVideoSectionFragmentFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { WatchVideoDialog } from '../../../media/WatchVideoDialog.client'
import { NextLink } from '../../../navigation/NextLink'
import { PageSection } from '../../PageSection.client'

import {
  ContentfulPageVideoSectionActions,
  ContentfulPageVideoSectionBackground,
  ContentfulPageVideoSectionContent,
  ContentfulPageVideoSectionSubtitle,
  ContentfulPageVideoSectionTitle,
  ContentfulPageVideoSectionTitleImage,
  ContentfulPageVideoSectionWrapper,
} from './ContentfulPageVideoSection.styles'

export const pageVideoSectionFragment = gql(`
  fragment PageVideoSectionFragment on VideoSection {
    buttonVariant
    id
    __typename
    name
    imageDesktop
    imageMobile
    inlineVideoDesktop
    inlineVideoMobile
    inlineVimeoIdDesktop
    inlineVimeoIdMobile
    linkHref1
    linkHref2
    linkText1
    linkText2
    modalVideoId
    modalVideoPlatform
    subtitle
    textColor
    textContentMaxWidthDesktop
    textContentMaxWidthMobile
    title
    titleImageDesktop
    titleImageMobile
  }
`)

export type ContentfulPageVideoSectionProps = {
  data: PageVideoSectionFragmentFragment
  pageSectionIndex: number
}

export const ContentfulPageVideoSection = ({
  data,
  pageSectionIndex,
}: ContentfulPageVideoSectionProps) => {
  const translations = useTranslation()

  const {
    textColor,
    title,
    subtitle,
    titleImageDesktop,
    titleImageMobile,
    textContentMaxWidthDesktop,
    textContentMaxWidthMobile,
    imageDesktop,
    imageMobile,
    inlineVideoDesktop,
    inlineVideoMobile,
    inlineVimeoIdDesktop,
    inlineVimeoIdMobile,
    linkText1,
    linkHref1,
    linkText2,
    linkHref2,
    buttonVariant: buttonVariantProp,
    modalVideoId,
    modalVideoPlatform,
  } = data

  const link2 =
    linkText2 && linkHref2
      ? {
          text: linkText2,
          href: linkHref2,
        }
      : undefined

  const modalVideo =
    modalVideoId && modalVideoPlatform
      ? {
          id: modalVideoId,
          platform: modalVideoPlatform,
        }
      : undefined

  const buttonVariant = isButtonVariant(buttonVariantProp)
    ? buttonVariantProp
    : 'black-on-white-no-border'

  return (
    <PageSection type={data.__typename} id={data.id}>
      <ContentfulPageVideoSectionWrapper>
        <ContentfulPageVideoSectionBackground>
          {imageDesktop ? (
            <ResponsiveImage
              src={imageDesktop}
              srcs={{
                sm: imageMobile ?? imageDesktop,
                md: imageDesktop,
              }}
              loading={pageSectionIndex < 2 ? 'default' : 'lazy'}
              fetchPriority={pageSectionIndex < 2 ? 'high' : 'low'}
              alt=''
              aspectRatios={null}
              widths={{
                unit: 'vw',
                xs: undefined,
                sm: 100,
                md: 100,
                lg: undefined,
                xl: undefined,
              }}
            />
          ) : null}
          <ClientOnlyResponsiveVideo
            inlineVideoDesktop={inlineVideoDesktop}
            inlineVideoMobile={inlineVideoMobile}
            inlineVimeoIdDesktop={inlineVimeoIdDesktop}
            inlineVimeoIdMobile={inlineVimeoIdMobile}
            playText={translations.magnolia.general.playVideo}
            pauseText={translations.magnolia.general.pauseVideo}
          />
        </ContentfulPageVideoSectionBackground>

        <ContentfulPageVideoSectionContent $textColor={textColor}>
          {/** Update the Contentful model to support actual images, not strings. So we can provide the aspect ratio. If we have aspectRatios set, we wouldn't need ContentfulPageVideoSectionTitleImage with its hardcoded height. */}
          {titleImageDesktop ? (
            <ContentfulPageVideoSectionTitleImage
              src={titleImageDesktop}
              srcs={{
                sm: titleImageMobile ?? titleImageDesktop,
                md: titleImageDesktop,
              }}
              transparentBackground
              loading='lazy'
              alt=''
              aspectRatios={null}
              widths={{
                unit: 'vw',
                xs: undefined,
                sm: 100,
                md: 100,
                lg: undefined,
                xl: undefined,
              }}
            />
          ) : null}
          {title && (
            <ContentfulPageVideoSectionTitle
              isUppercase={isUppercase(title)}
              $textContentMaxWidthMobile={textContentMaxWidthMobile}
              $textContentMaxWidthDesktop={textContentMaxWidthDesktop}
            >
              {title}
            </ContentfulPageVideoSectionTitle>
          )}
          {subtitle && (
            <ContentfulPageVideoSectionSubtitle
              isUppercase={isUppercase(subtitle)}
              $textContentMaxWidthMobile={textContentMaxWidthMobile}
              $textContentMaxWidthDesktop={textContentMaxWidthDesktop}
            >
              {subtitle}
            </ContentfulPageVideoSectionSubtitle>
          )}
          <ContentfulPageVideoSectionActions>
            {linkText1 && modalVideo ? (
              <WatchVideoDialog
                videoId={modalVideo.id}
                videoPlatform={modalVideo.platform}
                render={<Button variant={buttonVariant}>{linkText1}</Button>}
              />
            ) : linkText1 && linkHref1 ? (
              <Button as={NextLink} href={linkHref1} variant={buttonVariant}>
                {linkText1}
              </Button>
            ) : null}
            {link2 ? (
              <Button as={NextLink} href={link2.href} variant={buttonVariant}>
                {link2.text}
              </Button>
            ) : null}
          </ContentfulPageVideoSectionActions>
        </ContentfulPageVideoSectionContent>
      </ContentfulPageVideoSectionWrapper>
    </PageSection>
  )
}
